import { FC, useCallback, useMemo } from 'react';
import { PaymentNotificationSettingsResponse, PaymentNotificationType, PaymentQueries } from '@frontend/api-payments';
import { useTranslation } from '@frontend/i18n';
import { useMerchants } from '@frontend/payments-hooks';
import {
  Checkbox,
  ContentLoader,
  Table,
  TableColumnConfig,
  useAlert,
  useControlledField,
} from '@frontend/design-system';
import { DefaultNotificationSettings, PaymentSettingTypeList } from './constants';

interface PaymentNotificationSettingsProps {
  locationIds: string[];
}

type NotificationStatus = 'enabled' | 'disabled' | 'mixed';
interface NotificationSettingsData {
  type: PaymentNotificationType;
  label: string;
  popupNotificationStatus?: NotificationStatus;
}

export const PaymentNotificationSettings: FC<PaymentNotificationSettingsProps> = ({ locationIds = [] }) => {
  const { t } = useTranslation('payments', { keyPrefix: 'notification-settings' });
  const alerts = useAlert();
  const { invalidateNotificationSettings } = PaymentQueries.usePaymentsInvalidation();
  const { isLoading: isMerchantLoading } = useMerchants(locationIds);
  const notificationQueries = PaymentQueries.usePaymentNotificationSettingsQuery(locationIds);

  const { mutate, isLoading: isMutating } = PaymentQueries.usePaymentNotificationSetttingsMutate({
    onSuccess: () => {
      locationIds.forEach(invalidateNotificationSettings);
      alerts.success({ message: t('Notification settings updated successfully') });
    },
    onError: () => {
      alerts.error({ message: t('Failed to update notification settings') });
    },
  });

  const { isLoading, isRefetching, locationWiseSettingsData } = useMemo(() => {
    const isLoading = notificationQueries.some((query) => query.isLoading);
    const isRefetching = notificationQueries.some((query) => query.isRefetching);
    const paymentSettingsData = notificationQueries
      .map(({ data }) => data)
      .filter((data) => !!data) as PaymentNotificationSettingsResponse[];
    const locationWiseSettingsData = paymentSettingsData.reduce<Record<string, PaymentNotificationSettingsResponse>>(
      (acc, current) => ({ ...acc, [current.location_id]: { ...DefaultNotificationSettings, ...current } }),
      {}
    );

    return {
      isLoading,
      isRefetching,
      locationWiseSettingsData,
    };
  }, [notificationQueries]);

  const notificationSettingsData = useMemo<NotificationSettingsData[]>(() => {
    return PaymentSettingTypeList.map(({ type, label }) => {
      const activeCount = locationIds.reduce(
        (acc, locationId) => acc + (locationWiseSettingsData[locationId]?.[type] ? 1 : 0),
        0
      );
      return {
        type,
        label,
        popupNotificationStatus:
          activeCount === 0 ? 'disabled' : activeCount === locationIds.length ? 'enabled' : 'mixed',
      };
    });
  }, [locationWiseSettingsData, locationIds]);

  const toggleNotificationSettings = useCallback(
    (type: PaymentNotificationType, currentState: NotificationStatus) => {
      const updateState = currentState === 'disabled'; // set state to true if current disabled, false if mixed or enabled
      mutate({
        payload: locationIds.map((locationId) => {
          // Start with default settings to ensure all required fields are present
          const baseSettings = { ...DefaultNotificationSettings };
          // Add any existing settings from locationWiseSettingsData
          const existingSettings = locationWiseSettingsData[locationId] || {};
          // Return the combined settings with the updated type and location_id
          return {
            ...baseSettings,
            ...existingSettings,
            location_id: locationId,
            [type]: updateState,
          };
        }),
      });
    },
    [locationWiseSettingsData, locationIds]
  );

  const tableColumns = useMemo<TableColumnConfig<NotificationSettingsData>[]>(() => {
    return [
      {
        accessor: ({ label }) => label,
        id: 'paymentType',
        Header: t('Payment Type'),
        cellRenderer: (label) => label,
        disableSortBy: true,
        maxWidth: 200,
      },
      {
        accessor: ({ type, popupNotificationStatus }) => ({ popupNotificationStatus, type }),
        id: 'popupNotificationStatus',
        Header: t('Pop-up'),
        disableSortBy: true,
        cellRenderer: ({ type, popupNotificationStatus }) => (
          <PopupCell
            popupNotificationStatus={popupNotificationStatus}
            type={type}
            toggleNotificationSettings={toggleNotificationSettings}
          />
        ),
      },
    ];
  }, [toggleNotificationSettings]);

  return (
    <div css={{ position: 'relative' }}>
      <ContentLoader show={isMutating || isRefetching} />
      <Table<NotificationSettingsData>
        isLoading={isLoading || isMerchantLoading}
        data={notificationSettingsData}
        colConfig={tableColumns}
      />
    </div>
  );
};

type PopupCellProps = {
  popupNotificationStatus: NotificationStatus;
  type: PaymentNotificationType;
  toggleNotificationSettings: (type: PaymentNotificationType, currentState: NotificationStatus) => void;
};

const PopupCell = ({ popupNotificationStatus, type, toggleNotificationSettings }: PopupCellProps) => {
  const checkboxProps = useControlledField({
    type: 'checkbox',
    value: popupNotificationStatus !== 'disabled',
    onChange: () => {
      toggleNotificationSettings(type, popupNotificationStatus);
    },
  });
  return (
    <Checkbox
      {...checkboxProps}
      indeterminate={popupNotificationStatus === 'mixed'}
      name={`${type}-enable-checkbox`}
      error={false}
    />
  );
};
